import React, { useState, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { uploadAPI, recipeAPI } from '../services/api';

function RecipeCard({ recipe, onClick, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: recipe.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  // Handle click on card content (not drag handle)
  const handleContentClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    e.stopPropagation();
    setUploading(true);
    try {
      const response = await uploadAPI.uploadPhoto(file, recipe.id);
      // Update recipe image_url if this is the first image
      if (!recipe.image_url && response.data.photo_url) {
        await recipeAPI.update(recipe.id, { image_url: response.data.photo_url });
      }
      if (onUpdate) {
        await onUpdate();
      }
    } catch (err) {
      alert('Failed to upload image');
      console.error('Error uploading image:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = (e) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="recipe-card"
    >
      {/* Drag handle - only this area is draggable */}
      <div {...listeners} className="recipe-card-drag-handle" style={{ 
        position: 'absolute', 
        top: '8px', 
        right: '8px', 
        width: '20px', 
        height: '20px',
        cursor: 'grab',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#999',
        fontSize: '18px'
      }}>
        ⋮⋮
      </div>
      
      {/* Image upload button - appears on hover */}
      <div 
        className="recipe-card-upload-btn"
        onClick={handleUploadClick}
        style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          width: '32px',
          height: '32px',
          background: 'var(--primary-blue)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 10,
          opacity: 0,
          transition: 'opacity 0.2s',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
        title="Upload image"
      >
        {uploading ? (
          <span className="loading-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
        ) : (
          <span style={{ color: 'white', fontSize: '16px' }}>📷</span>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />
      
      {/* Clickable content area */}
      <div onClick={handleContentClick} style={{ cursor: 'pointer', paddingRight: '30px' }}>
        {recipe.image_url ? (
          <div style={{ position: 'relative' }}>
            <img src={recipe.image_url} alt={recipe.title} className="recipe-image" />
          </div>
        ) : (
          <div style={{
            width: '100%',
            height: '120px',
            background: 'var(--light-gray)',
            borderRadius: '6px',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '2rem'
          }}>
            📷
          </div>
        )}
        <div className="recipe-card-header">
          <h4 className="recipe-title">{recipe.title}</h4>
          {recipe.rating && (
            <span className="recipe-rating">{renderStars(recipe.rating)}</span>
          )}
        </div>
        {recipe.description && (
          <p className="recipe-description">{recipe.description}</p>
        )}
        <div className="recipe-meta">
          {recipe.prep_time && <span>⏱️ {recipe.prep_time}</span>}
          {recipe.servings && <span>🍽️ {recipe.servings}</span>}
        </div>
      </div>
    </div>
  );
}

export default RecipeCard;
